import moment from "moment";
import {
  eventDate,
  eventTimeLabel,
  rosterableEmployees,
  shopHoursForDate
} from "@/helpers/rosterV2";
import { isTimeAfternoon, isTimeEvening, isTimeMorning } from "@/helpers/time";

export function bookingDate(booking) {
  return eventDate(booking);
}

export function bookingStartTime(booking) {
  return eventTimeLabel(booking && booking.start);
}

export function bookingEndTime(booking) {
  return eventTimeLabel(booking && booking.end);
}

export function isCancelled(booking) {
  return !!(booking && booking.bookingStatus === "CANCELLED");
}

export function isRequest(booking) {
  return !!(booking && booking.bookingStatus === "UNCONFIRMED");
}

export function isConfirmed(booking) {
  return !!(booking && booking.bookingStatus === "CONFIRMED");
}

export function isUnpaid(booking) {
  return !!(
    isConfirmed(booking) &&
    booking.paymentStatus === "NOT_PAID"
  );
}

export function isLiveBooking(booking) {
  return !!(booking && !isCancelled(booking));
}

export function bookingKind(booking) {
  if (!booking || isCancelled(booking)) return "cancelled";
  if (isRequest(booking)) return "request";
  if (isUnpaid(booking)) return "unpaid";
  return "booked";
}

export function bookingKindLabel(kind) {
  if (kind === "request") return "To confirm";
  if (kind === "unpaid") return "Unpaid";
  if (kind === "cancelled") return "Cancelled";
  return "Booked";
}

export function customerName(booking) {
  const name = booking && booking.customer && booking.customer.name;
  return name ? String(name).trim() : "Customer";
}

export function customerPhone(booking) {
  return (booking && booking.customer && booking.customer.phoneNumber) || "";
}

export function customerEmail(booking) {
  return (booking && booking.customer && booking.customer.emailAddress) || "";
}

export function formatEuro(total) {
  if (total === null || total === undefined || total === "") return "";
  const value = Number(total);
  if (Number.isNaN(value)) return `€${total}`;
  const rounded = Math.round(value * 100) / 100;
  return rounded % 1 === 0 ? `€${rounded}` : `€${rounded.toFixed(2)}`;
}

export function timeRangeLabel(booking) {
  const start = bookingStartTime(booking);
  const end = bookingEndTime(booking);
  if (start && end) return `${start}–${end}`;
  return start || "";
}

export function bookingsOnDate(bookings, date) {
  return (bookings || []).filter(item => bookingDate(item) === date);
}

export function liveBookingsOnDate(bookings, date) {
  return bookingsOnDate(bookings, date).filter(isLiveBooking);
}

export function bookingsForColumn(bookings, resourceId, date) {
  return liveBookingsOnDate(bookings, date)
    .filter(item => item.resourceId === resourceId)
    .sort((a, b) => String(a.start).localeCompare(String(b.start)));
}

export function staffOnDate(staff, bookings, date) {
  const ids = new Set(
    liveBookingsOnDate(bookings, date).map(item => item && item.resourceId)
  );
  const known = (staff || []).filter(person => person && ids.has(person._id));
  const knownIds = new Set(known.map(person => person._id));
  const extras = [];
  ids.forEach(id => {
    if (id && !knownIds.has(id)) extras.push({ _id: id, name: "Staff" });
  });
  return known.concat(extras);
}

export function colorWash(hex, alpha) {
  const raw = String(hex || "").replace("#", "");
  const value = typeof alpha === "number" ? alpha : 0.1;
  if (raw.length !== 6) return `rgba(80, 70, 60, ${value})`;
  const r = parseInt(raw.slice(0, 2), 16);
  const g = parseInt(raw.slice(2, 4), 16);
  const b = parseInt(raw.slice(4, 6), 16);
  if ([r, g, b].some(n => Number.isNaN(n))) {
    return `rgba(80, 70, 60, ${value})`;
  }
  return `rgba(${r}, ${g}, ${b}, ${value})`;
}

export function incomingBookings(bookings) {
  return (bookings || [])
    .filter(isRequest)
    .sort((a, b) => String(a.start).localeCompare(String(b.start)));
}

export function minutesFromMidnight(hhmm) {
  if (!hhmm || typeof hhmm !== "string" || hhmm.indexOf(":") === -1) {
    return null;
  }
  const parts = hhmm.split(":").map(Number);
  if (parts.some(n => Number.isNaN(n))) return null;
  return parts[0] * 60 + parts[1];
}

export function padTime(minutes) {
  const safe = Math.max(0, Math.min(24 * 60, Number(minutes) || 0));
  const hour = Math.floor(safe / 60);
  const mins = safe % 60;
  return `${String(hour).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
}

export function dayWindow({ shop, date, bookings }) {
  const hours = shopHoursForDate(shop, date);
  let startMin = hours ? minutesFromMidnight(hours.start) : 9 * 60;
  let endMin = hours ? minutesFromMidnight(hours.end) : 18 * 60;
  if (startMin == null) startMin = 9 * 60;
  if (endMin == null) endMin = 18 * 60;

  (bookings || []).forEach(item => {
    if (isCancelled(item)) return;
    const start = minutesFromMidnight(bookingStartTime(item));
    const end = minutesFromMidnight(bookingEndTime(item));
    if (start != null && start < startMin) startMin = start;
    if (end != null && end > endMin) endMin = end;
  });

  if (endMin <= startMin) endMin = startMin + 60;

  return {
    start: padTime(startMin),
    end: padTime(endMin),
    startMin,
    endMin,
    span: endMin - startMin,
    open: !!hours
  };
}

export function hourMarks(window) {
  const data = window || { startMin: 9 * 60, endMin: 18 * 60, span: 9 * 60 };
  const marks = [];
  const first = Math.floor(data.startMin / 60);
  const last = Math.ceil(data.endMin / 60);
  for (let hour = first; hour <= last; hour += 1) {
    const minutes = hour * 60;
    marks.push({
      time: padTime(minutes),
      pct: data.span ? ((minutes - data.startMin) / data.span) * 100 : 0
    });
  }
  return marks;
}

export function timeMarks(window) {
  const data = window || { startMin: 9 * 60, endMin: 18 * 60, span: 9 * 60 };
  const marks = [];
  const first = Math.floor(data.startMin / 30) * 30;
  for (let minutes = first; minutes <= data.endMin; minutes += 30) {
    marks.push({
      time: padTime(minutes),
      pct: data.span ? ((minutes - data.startMin) / data.span) * 100 : 0,
      hour: minutes % 60 === 0
    });
  }
  return marks;
}

export function bookingMinutes(booking) {
  const start = minutesFromMidnight(bookingStartTime(booking));
  const end = minutesFromMidnight(bookingEndTime(booking));
  if (start == null || end == null) return 30;
  return Math.max(0, end - start);
}

export function bookingLayout(booking, window) {
  const data = window || { startMin: 0, span: 60 };
  const start = minutesFromMidnight(bookingStartTime(booking));
  const end = minutesFromMidnight(bookingEndTime(booking));
  const from = start == null ? data.startMin : start;
  const to = end == null ? from + 30 : end;
  const span = data.span || 60;
  return {
    top: ((from - data.startMin) / span) * 100,
    height: (Math.max(15, to - from) / span) * 100
  };
}

export function nowLinePct(window, nowTime) {
  const data = window || { startMin: 0, span: 60 };
  const minutes = minutesFromMidnight(nowTime);
  if (minutes == null || !data.span) return null;
  if (minutes < data.startMin || minutes > data.endMin) return null;
  return ((minutes - data.startMin) / data.span) * 100;
}

export function nowScrollTop({ railHeight, nowPct, viewHeight, headHeight }) {
  const rail = Number(railHeight) || 0;
  const view = Number(viewHeight) || 0;
  const head = Number(headHeight) || 0;
  if (!rail) return 0;
  const pct = nowPct == null ? 0 : nowPct;
  const nowY = (pct / 100) * rail;
  return Math.max(0, head + nowY - view * 0.32);
}

export function aroundNowBooking(bookings, nowTime) {
  const now = minutesFromMidnight(nowTime);
  if (now == null) return null;
  const live = (bookings || [])
    .filter(isLiveBooking)
    .sort((a, b) => String(a.start).localeCompare(String(b.start)));
  if (!live.length) return null;
  const current = live.find(item => {
    const start = minutesFromMidnight(bookingStartTime(item));
    const end = minutesFromMidnight(bookingEndTime(item));
    return start != null && end != null && start <= now && now < end;
  });
  if (current) return current;
  const next = live.find(item => {
    const start = minutesFromMidnight(bookingStartTime(item));
    return start != null && start >= now;
  });
  return next || live[live.length - 1];
}

export function dayInsight({ bookings, date }) {
  const live = liveBookingsOnDate(bookings, date);
  const requests = live.filter(isRequest);
  const confirmed = live.filter(isConfirmed);
  const unpaid = confirmed.filter(item => item.paymentStatus === "NOT_PAID");
  const take = confirmed.reduce((sum, item) => sum + (Number(item.total) || 0), 0);
  return {
    total: live.length,
    confirmed: confirmed.length,
    requests: requests.length,
    unpaid: unpaid.length,
    take
  };
}

export function weekInsight({ bookings, week, today }) {
  const dates = week || [];
  const totals = {
    confirmed: 0,
    requests: 0,
    unpaid: 0,
    take: 0,
    todayCount: 0
  };
  dates.forEach(date => {
    const day = dayInsight({ bookings, date });
    totals.confirmed += day.confirmed;
    totals.requests += day.requests;
    totals.unpaid += day.unpaid;
    totals.take += day.take;
    if (date === today) totals.todayCount = day.total;
  });
  return totals;
}

export function formatDayInsight(insight, { open } = {}) {
  if (open === false) {
    if (!insight || !insight.total) return "Closed";
    return `Closed · ${insight.total} still booked`;
  }
  const data = insight || {};
  if (!data.total) return "Quiet day";
  const parts = [
    data.total === 1 ? "1 booked" : `${data.total} booked`
  ];
  if (data.requests) {
    parts.push(
      data.requests === 1 ? "1 to confirm" : `${data.requests} to confirm`
    );
  }
  if (data.unpaid) {
    parts.push(data.unpaid === 1 ? "1 unpaid" : `${data.unpaid} unpaid`);
  }
  return parts.join(" · ");
}

export function formatWeekInsight(insight, { isThisWeek } = {}) {
  const data = insight || {};
  const parts = [];
  if (isThisWeek) {
    parts.push(
      data.todayCount === 1 ? "1 today" : `${data.todayCount} today`
    );
  } else {
    parts.push(
      data.confirmed === 1 ? "1 booked" : `${data.confirmed} booked`
    );
  }
  if (data.requests) {
    parts.push(
      data.requests === 1 ? "1 to confirm" : `${data.requests} to confirm`
    );
  }
  return parts.join(" · ");
}

export function subscribeRange(weekStart) {
  const start = weekStart;
  const end = moment
    .utc(weekStart, "YYYY-MM-DD", true)
    .add(7, "days")
    .format("YYYY-MM-DD");
  return {
    dateFrom: `${start}T00:00:00Z`,
    dateTo: `${end}T00:00:00Z`
  };
}

export function servicesForResource(services, resourceId) {
  const list = (services || []).filter(Boolean);
  if (!resourceId) return [];
  if (resourceId === "Any") return list;
  return list.filter(
    service =>
      Array.isArray(service.resources) &&
      service.resources.indexOf(resourceId) !== -1
  );
}

export function packageOptions(service) {
  if (!service) return [];
  const packages = service.packages || {};
  const order = Array.isArray(service.packagesDisplayOrder)
    ? service.packagesDisplayOrder
    : Object.keys(packages);
  return order.map(id => packages[id]).filter(Boolean);
}

export function durationMinutes(service) {
  if (!service) return 0;
  const duration = Number(service.duration) || 0;
  if (service.durationUnit === "HOURS") return duration * 60;
  return duration;
}

export function serviceParts(service, pkg) {
  return {
    service: (service && service.name) || "Service removed",
    package: pkg && pkg.name ? String(pkg.name) : ""
  };
}

export function serviceLine(service, pkg) {
  const parts = serviceParts(service, pkg);
  if (parts.package) return `${parts.package} · ${parts.service}`;
  return parts.service;
}

export function splitDayParts(times) {
  const morning = [];
  const afternoon = [];
  const evening = [];
  (times || []).forEach(time => {
    if (isTimeMorning(time)) morning.push(time);
    else if (isTimeEvening(time)) evening.push(time);
    else if (isTimeAfternoon(time)) afternoon.push(time);
  });
  return { morning, afternoon, evening };
}

export function bookableStaff(employees) {
  return rosterableEmployees(employees);
}

export function clickTimeFromRatio(window, ratio) {
  const data = window || { startMin: 9 * 60, span: 9 * 60 };
  const raw = data.startMin + Math.max(0, Math.min(1, ratio)) * data.span;
  const snapped = Math.round(raw / 15) * 15;
  return padTime(Math.max(data.startMin, Math.min(data.endMin, snapped)));
}

export { shopHoursForDate, rosterableEmployees };
